import { User } from 'oidc-client'
import { IAuthService } from '@/interfaces/IAuthService'
import store from '@/store/store'
import { USER_ACCOUNTS_DATA, USER_SETDATA, USER_SET_RULES } from '@/store/MUTATIONS-TYPES'
import { UserTypeService } from './UserTypeService'
import { AccountFetchingError, NotLoggedInError, UserContextError } from '@/utils/exceptions'
import { GuardianService } from './GuardianService'
import { AttaServices } from './attaServices'
import { User as UserIntf } from '@/interfaces/IUser'
import { clearAllCookies, getAuthToken, setCookie, clearCookie } from '@/utils/cookieUtils'
import { SentryPlugin } from '@/plugins/sentry'

/**
 * Auth service compliant with 5A standards.
 * Assembles user context to support requests to the internal/external BFF,
 * according to the user type.
 *
 * @class StandardAuthService
 * @implements {IAuthService}
 */
export class StandardAuthService implements IAuthService {
	userAccounts: UserIntf.IUserAccount[] | null = null

	async init(): Promise<void> {}

	async initializeUserContext(): Promise<void> {
		try {
			clearCookie('ATTA_USER_ID')

			this.userAccounts = await this.fetchUserAccounts()

			if (UserTypeService.isExternal() && !getAuthToken() && !import.meta.env.DEV) {
				throw new NotLoggedInError()
			}

			if (!this.userAccounts || this.userAccounts.length == 0) {
				this.redirectToAccountErrorPage()
			}

			SentryPlugin.addBreadcrumb({
				category: 'auth',
				message: 'User accounts fetched successfully',
				level: 'info',
				data: {
					accountCount: this.userAccounts.length,
					accountIds: this.userAccounts.map((a) => a.userId)
				}
			})

			const accountIndex = localStorage.getItem('ACCOUNT_SELECTED')
			const selectedAccount = accountIndex ? this.userAccounts[Number(accountIndex)] : this.userAccounts[0]

			if (!selectedAccount) {
				throw new UserContextError(
					`Account not found at index: ${accountIndex || 0}. Available accounts: ${this.userAccounts.length}`,
					'account_selection',
					{
						accountIndex: accountIndex || 'none',
						totalAccounts: this.userAccounts.length,
						accountIds: this.userAccounts.map((a) => a.userId)
					}
				)
			}

			await this.setActiveUserAccount(selectedAccount)
			await this.setUserAccounts(this.userAccounts)
		} catch (error) {
			if (error instanceof NotLoggedInError || error instanceof AccountFetchingError) {
				throw error
			}

			const operation = error instanceof UserContextError ? error.operation : 'initializeUserContext'
			const context = error instanceof UserContextError ? error.context : {}

			SentryPlugin.sendException(error, {
				tags: {
					component: 'StandardAuthService',
					operation,
					stage: 'user_context_initialization'
				},
				contexts: {
					userContext: {
						...context,
						totalAccounts: this.userAccounts?.length || 0,
						accountIds: this.userAccounts?.map((a) => a.userId) || []
					}
				}
			})

			throw error
		}
	}

	async login(): Promise<void> {
		if (UserTypeService.isExternal()) {
			let loginURI = import.meta.env.VUE_APP_5A_LOGIN + '?enableSocial=false&redirect=' + window.location.href
			window.location.href = loginURI
			throw new NotLoggedInError()
		}
	}

	async logout(): Promise<void> {
		localStorage.clear()
		sessionStorage.clear()
		clearAllCookies()

		window.location.href = UserTypeService.isInternal() ? '/logout' : import.meta.env.VUE_APP_5A_LOGIN
	}

	async fetchUserAccounts(): Promise<UserIntf.IUserAccount[]> {
		let accounts: UserIntf.IUserAccount[] = []
		try {
			accounts = await AttaServices.getUserAccounts()
		} catch (error) {
			console.error('Error fetching user accounts', error)
		}
		return accounts
	}

	async setActiveUserAccount(account: UserIntf.IUserAccount) {
		if (!account) {
			throw new UserContextError('Account parameter is null or undefined', 'setActiveUserAccount_validation')
		}

		SentryPlugin.addBreadcrumb({
			category: 'auth',
			message: 'Setting active user account',
			level: 'info',
			data: {
				userId: account.userId,
				email: account.email,
				franchiseId: account.franchiseId,
				partnerId: account.partnerId
			}
		})

		setCookie('ATTA_USER_ID', account.userId.toString())

		store.commit(USER_SETDATA, {
			sub: account.userId,
			name: account.fullName,
			email: account.email,
			franquiaid: account.franchiseId,
			parceiroid: account.partnerId,
			franquiausuarioisexpert: account.isExpertFranchise,
			isanalita: account.isAnalyst
		})

		SentryPlugin.addBreadcrumb({
			category: 'auth',
			message: 'User data committed to store',
			level: 'info'
		})

		await this.setUserRules()
	}

	async setUserAccounts(userAccounts: Array<UserIntf.IUserAccount>) {
		store.commit(USER_ACCOUNTS_DATA, userAccounts)

		SentryPlugin.addBreadcrumb({
			category: 'auth',
			message: 'User accounts committed to store',
			level: 'info',
			data: {
				count: userAccounts.length
			}
		})
	}

	async setUserRules() {
		const response = await GuardianService.getCurrentUserRules()

		SentryPlugin.addBreadcrumb({
			category: 'auth',
			message: 'User rules fetched',
			level: response.error ? 'warning' : 'info',
			data: {
				hasError: !!response.error,
				hasResult: !!response.result
			}
		})

		if (response.error) {
			const errorMsg = `GuardianService returned error while fetching user rules`
			console.error(errorMsg, response.error)
			SentryPlugin.sendMessage(errorMsg)
			return
		}

		store.commit(USER_SET_RULES, response.result)

		SentryPlugin.addBreadcrumb({
			category: 'auth',
			message: 'User rules committed to store',
			level: 'info'
		})
	}

	redirectToAccountErrorPage() {
		throw new AccountFetchingError()
	}

	async getUser(): Promise<User | null> {
		console.warn('StandardAuthService: getUser() is deprecated. Consider using store.user directly.')

		const storeUser = store.state.user
		if (!storeUser) return null

		return {
			profile: {
				sub: storeUser.id?.toString(),
				name: storeUser.name,
				email: storeUser.email,
				tipoacesso: storeUser.tipoAcesso?.toString(),
				franquiaid: storeUser.franquiaId?.toString(),
				parceiroid: storeUser.parceiroId?.toString(),
				grupo: storeUser.grupo?.toString(),
				role: storeUser.role?.toString(),
				franquiausuarioisexpert: storeUser.franquiaUsuarioIsExpert?.toString(),
				isanalita: storeUser.isAnalista?.toString()
			}
		} as unknown as User
	}

	async setUserStore(user?: User | null): Promise<User | null> {
		console.error('StandardAuthService: setUserStore() is deprecated.')
		return null
	}

	async authorizeByEncryptedId(id: string): Promise<string> {
		throw new Error('StandardAuthService: authorizeByEncryptedId() is deprecated. This method is no longer supported.')
	}

	async GetUserByEncryptedId(id: string): Promise<User> {
		throw new Error('StandardAuthService: GetUserByEncryptedId() is deprecated. This method is no longer supported.')
	}

	async getUserByToken(token: string): Promise<User> {
		throw new Error('StandardAuthService: getUserByToken() is deprecated. This method is no longer supported.')
	}

	async refreshToken(): Promise<any> {
		throw new Error('StandardAuthService: refreshToken() is deprecated. This method is no longer supported.')
	}

	async setPassword(cpf: string): Promise<any> {
		throw new Error('StandardAuthService: setPassword() is deprecated. This method is no longer supported.')
	}
}
